import { CHECKOUT_ITEM_TYPE_ACCOMMODATION_V2, CHECKOUT_ITEM_TYPE_BEDBANK, CHECKOUT_ITEM_TYPE_BUNDLE_AND_SAVE, CHECKOUT_ITEM_TYPE_LE_HOTEL, CHECKOUT_ITEM_TYPE_TOUR_V1 } from 'constants/checkout'
import { RESERVATION_TYPE_BOOK_LATER, RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import uuidV4 from 'lib/string/uuidV4Utils'
import { pickGhaPriceMatch, type GhaPriceMatchClickThrough } from 'lib/metasearch/ghaPriceMatch'
import type { PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { addGTMEvent } from 'api/googleTagManager'
import { addAccommodationToCheckout, addBundleAndSaveToCheckout } from 'checkout/lib/analytics/checkoutUAMappers'
import { nonNullable } from 'lib/array/arrayUtils'
import type { BundleOfferPackageOptionView } from 'selectors/offerPage/bundledOfferPageSelectors'
import { getAccommodationV2CartPricing } from 'lib/accommodation/buildEnquirySessionPayload'

type Period = { checkIn: string, checkOut: string, duration?: number }

export function mapPropertyRateRef(rate: App.BasePropertyRate, bundleId?: string): App.PropertyRateRef {
  return {
    propertyId: rate.propertyId,
    roomId: rate.roomId,
    serviceRateId: rate.service.rateId,
    ...(rate.service.type === 'lpc' && {
      servicePackageId: rate.service.packageId,
      serviceOfferId: rate.service.offerId,
    }),
    groupKey: rate.groupKey,
    bundleId,
    serviceType: rate.service.type,
  }
}

/**
 * Resolves the bundle tagging for a parent accommodation item.
 *
 * A rate cannot carry both a mandatory bundle and optional bundles at the same time. Thus, an unset
 * `bundleOptin` with a singleton `accommBundleIds` implies a mandatory bundle, and an unset
 * `bundleOptin` with multiple `accommBundleIds` is invalid.
 *
 * Order service uses `(itemId, accommBundleIds)` on parent accommodation items and
 * `(bundledItemIds, isAccomBundle)` on child experience/transfer items to determine bundle "groups"
 * (where cancelling one item cascades to the other items in the group).
 */
function resolveAccommodationBundleTagging(
  rate: App.EnrichedPropertyRate,
  selectedOptionalBundles?: Array<Pick<App.Property.OptionalBundle, 'bundleId'>>,
): { bundleId?: string, bundleOptin?: true, accommBundleIds?: Array<string> } {
  if (rate.bundle) return { bundleId: rate.bundle.bundleId, accommBundleIds: [rate.bundle.bundleId] }
  if (!selectedOptionalBundles?.length) return {}
  return {
    bundleId: selectedOptionalBundles[0].bundleId,
    bundleOptin: true,
    accommBundleIds: selectedOptionalBundles.map(({ bundleId }) => bundleId),
  }
}

export function mapPropertyRateUpgradeBooking(rate: App.EnrichedPropertyRate): App.Checkout.PropertyRateUpgradeBooking {
  if (rate.service.type === 'bedbank') {
    return {
      offerId: rate.service.propertyId,
      roomId: rate.service.roomId,
      rateGroupId: rate.service.rateGroupId,
      bedGroupId: rate.service.bedGroups[0]?.id,
      boardCode: rate.boardCode,
      isFlightBundle: rate.isFlightBundle,
      refundable: rate.headlineCancellationPolicyType === 'fullyRefundable',
    }
  }
  return {
    offerId: rate.service.offerId,
    paymentScheduleInfo: rate.paymentScheduleInfo,
  }
}

function mapPropertyRatePricing(rate: App.EnrichedPropertyRate): App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing'] {
  return {
    totals: rate.totals,
    ...(rate.luxPlusTotals && { luxPlusTotals: rate.luxPlusTotals }),
  }
}

export function withPropertyRateUpgradeSessions(
  upgrade: App.Checkout.PropertyRateLoyaltyUpgrade,
  sessions: {
    sourceSessionId?: string
    targetSessionId?: string
  },
): App.Checkout.PropertyRateLoyaltyUpgrade {
  return {
    ...upgrade,
    ...(upgrade.sourceBooking && {
      sourceBooking: {
        ...upgrade.sourceBooking,
        ...(sessions.sourceSessionId && { sessionId: sessions.sourceSessionId }),
      },
    }),
    ...(upgrade.targetBooking && {
      targetBooking: {
        ...upgrade.targetBooking,
        ...(sessions.targetSessionId && { sessionId: sessions.targetSessionId }),
      },
    }),
  }
}

export function createPropertyRateLoyaltyUpgrade(
  sourceRate: App.EnrichedPropertyRate,
  targetRate: App.EnrichedPropertyRate,
  sourceIsUltraLux?: boolean,
  targetImage?: App.Image,
  // Applied ⇒ the upgrade was selected on the offer page; not applied ⇒ it's only *available*, so
  // checkout can offer it. Checkout toggles this either way.
  applied: boolean = true,
): App.Checkout.PropertyRateLoyaltyUpgrade {
  return {
    appliedUpgradeType: 'package',
    applied,
    source: mapPropertyRateRef(sourceRate, sourceRate.bundle?.bundleId),
    target: mapPropertyRateRef(targetRate, targetRate.bundle?.bundleId),
    sourceName: sourceRate.name,
    sourcePricing: mapPropertyRatePricing(sourceRate),
    targetPricing: mapPropertyRatePricing(targetRate),
    sourceBooking: mapPropertyRateUpgradeBooking(sourceRate),
    targetBooking: mapPropertyRateUpgradeBooking(targetRate),
    ...(targetImage && { targetImage }),
    // Captured so checkout can rebuild the source LPC earn key (ultra-lux changes it) and earn as the base rate.
    ...(sourceIsUltraLux !== undefined && { sourceIsUltraLux }),
    // Source bedbank supplier code — the EPS earn key is `propertyId:supplierCode` and the booked target may differ.
    ...(sourceRate.service.type === 'bedbank' && { sourceSupplierCode: sourceRate.service.supplierCode }),
  }
}

export function generateMultipleRoomCheckoutItems(
  selectedPackagesByRoom: PackageOptionViewByRoom,
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary,
  dates: Period,
  roomOccupants: Array<App.RoomOccupants>,
  currencyCode: string,
): Array<App.Checkout.InstantBookingLEHotelItem> {
  return nonNullable(Object.entries(selectedPackagesByRoom).map(([roomId, packageOption]) => {
    const occupancy = roomOccupants.find(room => room.id === roomId)
    if (packageOption && occupancy) {
      const reservationType = RESERVATION_TYPE_INSTANT_BOOKING

      addGTMEvent(addAccommodationToCheckout(offer, packageOption, reservationType, currencyCode))

      return ({
        itemId: uuidV4(),
        offerId: offer.id,
        transactionKey: uuidV4(),
        itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
        reservationType,
        packageId: packageOption.package.id,
        roomRateId: packageOption.package.roomRate.id,
        duration: packageOption.package.duration,
        occupancy,
        ...dates,
      })
    }
    return undefined
  }))
}

export function generateBundleAndSaveCheckoutItems(
  offer: App.BundleOffer,
  packageOption: BundleOfferPackageOptionView,
  dates: Record<string, Required<Period>>,
  roomOccupants: Array<App.Occupants>,
  currencyCode: string,
): Array<App.Checkout.BundleAndSaveItem> {
  return roomOccupants.map((occupancy): App.Checkout.BundleAndSaveItem => {
    addGTMEvent(addBundleAndSaveToCheckout(offer, packageOption, currencyCode))

    return {
      itemId: uuidV4(),
      offerId: offer.id,
      transactionKey: uuidV4(),
      itemType: CHECKOUT_ITEM_TYPE_BUNDLE_AND_SAVE,
      packageId: packageOption.bundlePackageId,
      occupancy,
      dates,
    }
  })
}

export function generateVillaCheckoutItem(
  offer: App.VillaOffer | App.VillaOfferSummary,
  packageOption: App.OfferPackageOptionView,
  dates: Period,
  occupancy: App.Occupants,
  currencyCode: string,
): App.Checkout.VillaItem {
  const reservationType = RESERVATION_TYPE_INSTANT_BOOKING

  addGTMEvent(addAccommodationToCheckout(offer, packageOption, reservationType, currencyCode))

  return {
    itemId: uuidV4(),
    offerId: offer.id,
    transactionKey: uuidV4(),
    itemType: 'villa',
    reservationType,
    packageId: packageOption.package.id,
    roomRateId: packageOption.package.roomRate.id,
    duration: packageOption.package.duration,
    occupancy,
    ...dates,
  }
}

interface GenerateAccommodationCheckoutItemsOptions {
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary
  packageOption: App.OfferPackageOptionView
  dates: Period
  roomOccupants: Array<App.Occupants>
  currencyCode: string
  loyaltyAppliedUpgradeType?: App.LoyaltyStayUpgradeType
  basePackageId?: string,
  bypassRegionFilter?: boolean
  bypassRegionSettingId?: string
}
export function generateAccommodationCheckoutItems({
  offer,
  packageOption,
  dates,
  roomOccupants,
  currencyCode,
  loyaltyAppliedUpgradeType,
  basePackageId,
  bypassRegionFilter,
  bypassRegionSettingId,
}: GenerateAccommodationCheckoutItemsOptions): Array<App.Checkout.InstantBookingLEHotelItem> {
  return roomOccupants.map((occupancy): App.Checkout.InstantBookingLEHotelItem => {
    const reservationType = RESERVATION_TYPE_INSTANT_BOOKING

    addGTMEvent(addAccommodationToCheckout(offer, packageOption, reservationType, currencyCode))

    return {
      itemId: uuidV4(),
      offerId: offer.id,
      transactionKey: uuidV4(),
      itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
      reservationType,
      packageId: packageOption.package.id,
      roomRateId: packageOption.package.roomRate.id,
      duration: packageOption.package.duration,
      occupancy,
      loyaltyAppliedUpgradeType,
      loyaltyBasePackageId: loyaltyAppliedUpgradeType ? basePackageId : undefined,
      bypassRegionFilter: bypassRegionFilter || undefined,
      bypassRegionSettingId: bypassRegionSettingId || undefined,
      ...dates,
    }
  })
}

export function generateBedbankAccommodationCheckoutItems(
  offer: App.BedbankOffer,
  pkg: App.BedbankPackage,
  rate: App.BedbankRate,
  dates: Required<Period>,
  roomOccupants: Array<App.Occupants>,
  sessionId?: string,
  bundledItemIds?: Array<string>,
  gha?: GhaPriceMatchClickThrough,
): Array<App.Checkout.BedbankHotelItem> {
  return roomOccupants.map<App.Checkout.BedbankHotelItem>(occupancy => ({
    offerId: offer.id,
    sessionId,
    gha: gha && pickGhaPriceMatch(gha),
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_BEDBANK,
    roomId: pkg.roomId,
    roomRateId: rate.id,
    rateGroupId: rate.groupId,
    token: rate.token,
    bedGroupId: rate.bedGroups[0].id,
    isFlightBundle: rate.isFlightBundle,
    boardCode: rate.boardCode,
    refundable: rate.refundable,
    occupancy,
    ...dates,
    bundledItemIds,
  }))
}

export function generateLpcPropertyAccommodationCheckoutItems(
  rate: App.EnrichedLpcPropertyRate,
  dates: Required<Period>,
  roomOccupants: Array<App.Occupants>,
  loyalty?: App.Checkout.PropertyRateLoyaltyUpgrade,
  selectedOptionalBundles?: Array<Pick<App.Property.OptionalBundle, 'bundleId'>>,
): Array<App.Checkout.InstantBookingLEHotelItem> {
  const { bundleId, bundleOptin, accommBundleIds } = resolveAccommodationBundleTagging(rate, selectedOptionalBundles)
  // Not-applied upgrades ride along as metadata only — the `loyaltyApplied*` fields must stay unset
  // or the base-rate item would read as upgraded (mirrors `buildToggledPropertyRateItem`'s removed shape).
  const appliedLoyaltyUpgrade = loyalty?.applied !== false ? loyalty : undefined
  return roomOccupants.map(occupancy => {
    const shouldAttachPropertyRateRef = !!(bundleId || loyalty)
    return {
      itemId: uuidV4(),
      offerId: rate.service.offerId,
      transactionKey: uuidV4(),
      itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
      reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
      packageId: rate.service.packageId,
      roomRateId: rate.service.rateId,
      paymentScheduleInfo: rate.paymentScheduleInfo,
      loyaltyAppliedUpgradeType: appliedLoyaltyUpgrade?.appliedUpgradeType,
      loyaltyBasePackageId: appliedLoyaltyUpgrade?.source.servicePackageId,
      loyaltyBaseRateId: appliedLoyaltyUpgrade?.source.serviceRateId,
      loyaltyPropertyRateUpgrade: loyalty,
      ...(rate.availabilitySessionId && {
        accommAvailabilitySessionId: rate.availabilitySessionId,
      }),
      ...(rate.promotion?.isCampaign && rate.promotion.campaignId && {
        accommCampaignId: rate.promotion.campaignId,
      }),
      ...(bundleId && {
        ...(bundleOptin && { bundleOptin }),
        ...(accommBundleIds && { accommBundleIds }),
      }),
      ...(shouldAttachPropertyRateRef && {
        propertyRateRef: mapPropertyRateRef(rate, bundleOptin ? undefined : bundleId),
      }),
      occupancy,
      ...dates,
    }
  })
}

export function generateBedbankPropertyAccommodationCheckoutItems(
  sessionId: string | undefined,
  rate: App.EnrichedBedbankPropertyRate,
  dates: Required<Period>,
  roomOccupants: Array<App.Occupants>,
  loyalty?: App.Checkout.PropertyRateLoyaltyUpgrade,
  flightDestinationAirportCode?: string,
  selectedOptionalBundles?: Array<Pick<App.Property.OptionalBundle, 'bundleId'>>,
  gha?: GhaPriceMatchClickThrough,
): Array<App.Checkout.BedbankHotelItem> {
  const { bundleId, bundleOptin, accommBundleIds } = resolveAccommodationBundleTagging(rate, selectedOptionalBundles)
  const appliedLoyaltyUpgrade = loyalty?.applied !== false ? loyalty : undefined
  return roomOccupants.map<App.Checkout.BedbankHotelItem>(occupancy => ({
    offerId: rate.service.propertyId,
    sessionId,
    gha: gha && pickGhaPriceMatch(gha),
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_BEDBANK,
    roomId: rate.service.roomId,
    roomRateId: rate.service.rateId,
    rateGroupId: rate.service.rateGroupId,
    token: rate.service.token,
    bedGroupId: rate.service.bedGroups[0].id,
    loyaltyAppliedUpgradeType: appliedLoyaltyUpgrade?.appliedUpgradeType,
    loyaltyBasePackageId: appliedLoyaltyUpgrade?.source.servicePackageId,
    loyaltyBaseRateId: appliedLoyaltyUpgrade?.source.serviceRateId,
    loyaltyPropertyRateUpgrade: loyalty,
    flightDestinationAirportCode,
    isFlightBundle: rate.isFlightBundle,
    boardCode: rate.boardCode,
    refundable: rate.headlineCancellationPolicyType === 'fullyRefundable',
    ...(rate.availabilitySessionId && {
      accommAvailabilitySessionId: rate.availabilitySessionId,
    }),
    ...(rate.promotion?.isCampaign && rate.promotion.campaignId && {
      accommCampaignId: rate.promotion.campaignId,
    }),
    ...(bundleId && {
      ...(bundleOptin && { bundleOptin }),
      ...(accommBundleIds && { accommBundleIds }),
    }),
    // Both kinds carry a ref for rate lookup; only mandatory carries the bundleId on it — see the
    // LPC generator above.
    ...((bundleId || loyalty) && {
      propertyRateRef: mapPropertyRateRef(rate, bundleOptin ? undefined : bundleId),
    }),
    occupancy,
    ...dates,
  }))
}

/** Reads identifiers only, so a raw (unenriched) rate works too. */
export function getAccommodationV2RateIdentity(rate: App.BasePropertyRate): {
  offerId: string
  internalService: App.Checkout.AccommodationV2Item['internalService']
  internalRateId: string
  rateGroupId: string | undefined
  bedGroupId: string | undefined
  internalPropertyId: string
  internalRoomId: string
  ratePlanId: string | undefined
} {
  if (rate.service.type === 'bedbank') {
    return {
      offerId: rate.service.propertyId,
      internalService: 'bedbank',
      internalRateId: rate.service.rateId,
      rateGroupId: rate.service.rateGroupId,
      bedGroupId: rate.service.bedGroups[0]?.id,
      internalPropertyId: rate.service.propertyId,
      internalRoomId: rate.service.roomId,
      ratePlanId: undefined,
    }
  }

  return {
    offerId: rate.service.offerId,
    internalService: 'reservation',
    internalRateId: rate.service.rateId,
    rateGroupId: rate.groupKey,
    bedGroupId: undefined,
    internalPropertyId: rate.service.propertyId,
    internalRoomId: rate.service.roomId,
    ratePlanId: rate.service.ratePlanId,
  }
}

/**
 * Every rate field, each assigned even when absent, so spreading one side's fields over an item clears
 * the other side's values.
 */
type AllAccommodationV2RateFields = {
  [K in keyof Required<App.Checkout.AccommodationV2RateFields>]: App.Checkout.AccommodationV2RateFields[K]
}

/**
 * A rate's accomV2 item fields, for a loyalty package upgrade's toggle. The rate has no session yet.
 */
export function getAccommodationV2RateFields(rate: App.EnrichedPropertyRate): AllAccommodationV2RateFields {
  const pricing = getAccommodationV2CartPricing(rate)

  return {
    sessionId: undefined,
    ...getAccommodationV2RateIdentity(rate),
    roomId: rate.roomId,
    optionId: rate.optionId,
    sellTotal: pricing.sellTotal,
    currency: pricing.currency,
    propertyFees: pricing.propertyFees,
    luxPlusTotal: pricing.luxPlusTotal,
    paymentMethod: rate.paymentMethod.type,
    token: rate.token || undefined,
    isFlightBundle: rate.isFlightBundle,
    boardCode: rate.boardCode,
    accommAvailabilitySessionId: rate.availabilitySessionId || undefined,
    accommCampaignId: rate.promotion?.isCampaign ? rate.promotion.campaignId || undefined : undefined,
    propertyRateRef: mapPropertyRateRef(rate),
  }
}

/** An accomV2 item's own rate fields, so a toggle can keep the side it leaves. */
export function pickAccommodationV2RateFields(item: App.Checkout.AccommodationV2Item): AllAccommodationV2RateFields {
  return {
    sessionId: item.sessionId,
    offerId: item.offerId,
    roomId: item.roomId,
    optionId: item.optionId,
    internalRateId: item.internalRateId,
    internalService: item.internalService,
    internalPropertyId: item.internalPropertyId,
    internalRoomId: item.internalRoomId,
    ratePlanId: item.ratePlanId,
    rateGroupId: item.rateGroupId,
    bedGroupId: item.bedGroupId,
    sellTotal: item.sellTotal,
    currency: item.currency,
    propertyFees: item.propertyFees,
    luxPlusTotal: item.luxPlusTotal,
    paymentMethod: item.paymentMethod,
    token: item.token,
    isFlightBundle: item.isFlightBundle,
    boardCode: item.boardCode,
    accommAvailabilitySessionId: item.accommAvailabilitySessionId,
    accommCampaignId: item.accommCampaignId,
    propertyRateRef: item.propertyRateRef,
  }
}

/**
 * Builds one accommodationV2 cart item for a single occupancy on the svc-accommodation booking path.
 * Callers map over rooms and pass the matching sessionId per occupancy.
 */
export function generateAccommodationV2CheckoutItem(
  rate: App.EnrichedPropertyRate,
  dates: Required<Period>,
  occupancy: App.Occupants,
  sessionId?: string,
  loyalty?: App.Checkout.PropertyRateLoyaltyUpgrade,
  flightDestinationAirportCode?: string,
  selectedOptionalBundles?: Array<Pick<App.Property.OptionalBundle, 'bundleId'>>,
): App.Checkout.AccommodationV2Item {
  const { bundleId, bundleOptin, accommBundleIds } = resolveAccommodationBundleTagging(rate, selectedOptionalBundles)
  // Not-applied upgrades ride along as metadata only — the `loyaltyApplied*` fields must stay unset
  // or the base-rate item would read as upgraded (mirrors LPC/bedbank property generators).
  const appliedLoyaltyUpgrade = loyalty?.applied !== false ? loyalty : undefined
  const {
    offerId,
    internalService,
    internalRateId,
    rateGroupId,
    bedGroupId,
    internalPropertyId,
    internalRoomId,
    ratePlanId,
  } = getAccommodationV2RateIdentity(rate)
  const pricing = getAccommodationV2CartPricing(rate)

  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_ACCOMMODATION_V2,
    offerId,
    sessionId,
    propertyId: rate.propertyId,
    roomId: rate.roomId,
    optionId: rate.optionId,
    internalRateId,
    internalService,
    internalPropertyId,
    internalRoomId,
    ratePlanId,
    rateGroupId,
    bedGroupId,
    sellTotal: pricing.sellTotal,
    currency: pricing.currency,
    ...(pricing.propertyFees != null ? { propertyFees: pricing.propertyFees } : {}),
    ...(pricing.surchargePayableAtProperty != null
      ? { surchargePayableAtProperty: pricing.surchargePayableAtProperty }
      : {}),
    ...(pricing.luxPlusTotal != null ? { luxPlusTotal: pricing.luxPlusTotal } : {}),
    ...(rate.totals.channelMarkupLeadPricing && { channelMarkupLeadPricing: rate.totals.channelMarkupLeadPricing }),
    paymentMethod: rate.paymentMethod.type,
    ...(rate.token ? { token: rate.token } : {}),
    loyaltyAppliedUpgradeType: appliedLoyaltyUpgrade?.appliedUpgradeType,
    loyaltyBasePackageId: appliedLoyaltyUpgrade?.source.servicePackageId,
    loyaltyBaseRateId: appliedLoyaltyUpgrade?.source.serviceRateId,
    loyaltyPropertyRateUpgrade: loyalty,
    flightDestinationAirportCode,
    isFlightBundle: rate.isFlightBundle,
    boardCode: rate.boardCode,
    ...(rate.availabilitySessionId && {
      accommAvailabilitySessionId: rate.availabilitySessionId,
    }),
    ...(rate.promotion?.isCampaign && rate.promotion.campaignId && {
      accommCampaignId: rate.promotion.campaignId,
    }),
    ...(bundleId && {
      ...(bundleOptin && { bundleOptin }),
      ...(accommBundleIds && { accommBundleIds }),
    }),
    ...((bundleId || loyalty) && {
      propertyRateRef: mapPropertyRateRef(rate, bundleOptin ? undefined : bundleId),
    }),
    occupancy,
    ...dates,
  }
}

interface BedbankChangeDatesItemsOptions {
  orderId: string
  orderItemId: string
  offerId: string
  roomId: string
  roomRateId: string
  rateGroupId?: string
  token?: string
  isFlightBundle: boolean
  reduceDatesOnly?: boolean
  /**
   * Bundle linkage of the order item being replaced. Without it the replacement item is
   * created unbundled: no cancellation cascade onto the bundled children, no refund or
   * invoice fold, and an orphaned child. The group is the load-bearing part -- it ties
   * the replacement to ALL of the original's children.
   */
  bundle?: {
    accommBundleIds?: Array<string>
    bundleGroupId?: string
    bundleOptin?: boolean
  }
  dates: { checkIn: string, checkOut: string, duration: number }
  roomsOptions: Array<{
    occupancy: App.Occupants
    boardCode: string
    refundable: boolean
    bedGroupId: string
  }>
}

/**
 * Maps an order item's bundle linkage into the `bundle` option for
 * generateBedbankChangeDatesAccommodationCheckoutItems. Every date-change /
 * room-upgrade entry point must spread this rather than hand-rolling the
 * fields -- if a new field ever needs carrying, this is the only place to add it.
 */
export function getBedbankChangeDatesBundleLinkage(
  orderItem: Pick<App.OrderBedbankItem, 'accommBundleIds' | 'bundleGroupId' | 'bundleOptin'>,
): Pick<BedbankChangeDatesItemsOptions, 'bundle'> {
  return orderItem.accommBundleIds?.length ? {
    bundle: {
      accommBundleIds: orderItem.accommBundleIds,
      bundleGroupId: orderItem.bundleGroupId,
      bundleOptin: orderItem.bundleOptin,
    },
  } : {}
}

export function generateBedbankChangeDatesAccommodationCheckoutItems({
  orderId,
  orderItemId,
  offerId,
  roomId,
  roomRateId,
  rateGroupId,
  token,
  isFlightBundle,
  reduceDatesOnly,
  bundle,
  dates,
  roomsOptions,
}: BedbankChangeDatesItemsOptions): Array<App.Checkout.BedbankHotelItem> {
  return roomsOptions.map<App.Checkout.BedbankHotelItem>(room => ({
    ...(bundle && {
      accommBundleIds: bundle.accommBundleIds,
      bundleGroupId: bundle.bundleGroupId,
      bundleOptin: bundle.bundleOptin,
    }),
    offerId,
    itemId: uuidV4(),
    orderItemId,
    orderId,
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_BEDBANK,
    roomId,
    roomRateId,
    bedGroupId: room.bedGroupId,
    rateGroupId,
    token,
    isFlightBundle,
    reduceDatesOnly,
    occupancy: room.occupancy,
    boardCode: room.boardCode,
    refundable: room.refundable,
    ...dates,
  }))
}

export function generateBNBLCheckoutItem(
  offer: App.Offer,
  packageOption: App.Package,
  occupancies: Array<App.Occupants> = [{ adults: 1 }],
): Array<App.Checkout.BNBLLEHotelItem> {
  return occupancies.map(occupancy => ({
    offerId: offer.id,
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
    reservationType: RESERVATION_TYPE_BOOK_LATER,
    packageId: packageOption.id,
    roomRateId: packageOption.roomRate!.id,
    duration: packageOption.duration,
    occupancy,
  }))
}

export function generateSelectDateCheckoutItem(
  offer: App.Offer,
  packageOption: App.Package,
  orderId: string,
  orderItemId: string,
  occupancy?: App.Occupants,
): App.Checkout.BNBLLEHotelSetDatesItem {
  return {
    itemId: uuidV4(),
    offerId: offer.id,
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
    reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
    packageId: packageOption.id,
    roomRateId: packageOption.roomRate!.id,
    duration: packageOption.duration,
    orderItemId,
    orderId,
    ...(occupancy ? { occupancy } : {}),
  }
}

export function generateTourCheckoutItem(
  offer: App.Offer,
  pkg: App.Package,
  occupancy: App.Occupants,
  options: {
    dates?: { startDate: string, endDate: string }
  } = {},
): App.Checkout.LETourV1Item {
  return {
    itemId: uuidV4(),
    offerId: offer.id,
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_TOUR_V1,
    packageId: pkg.id,
    duration: pkg.duration,
    occupancy,
    startDate: options.dates?.startDate,
    endDate: options.dates?.endDate,
  }
}

export function generatePackageUpgradeItem(
  offer: App.Offer | App.OfferSummary,
  order: App.Order,
  orderItem: App.OrderItem,
  bypassRegionFilter?: boolean,
  bypassRegionSettingId?: string,
): App.Checkout.InstantBookingLEHotelItem {
  const pkgOption = orderItem.package as App.OrderItemHotelPackage
  const reservation = orderItem.reservation

  if (!reservation) {
    throw new Error('Reservation is required for package upgrade')
  }

  const occupancy: App.Occupants = {
    adults: reservation.adults,
    teenagers: reservation.teenagers,
    children: reservation.children,
    infants: reservation.infants,
    childrenAge: reservation.childrenAge,
  }

  return {
    itemId: uuidV4(),
    offerId: offer.id,
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
    reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
    packageId: pkgOption.id,
    roomRateId: pkgOption.roomRateId,
    dynamicPricingRoomTypeId: orderItem.dynamicPricingRoomTypeId,
    dynamicPricingRoomRateId: orderItem.dynamicPricingRoomRateId,
    duration: pkgOption.duration,
    checkIn: reservation.startDate,
    checkOut: reservation.endDate,
    occupancy,
    orderId: order.id,
    orderItemId: orderItem.id,
    isUpdated: false,
    // Prefer the durable values persisted on the order item; the params act as a
    // same-session fallback until svc-order returns them.
    bypassRegionFilter: (orderItem.bypassRegionFilter || bypassRegionFilter) || undefined,
    bypassRegionSettingId: orderItem.bypassRegionSettingId || bypassRegionSettingId || undefined,
  }
}

export function generateChangeDatesItem(
  offer: App.Offer | App.OfferSummary,
  order: App.Order,
  orderItem: App.OrderItem,
  selectedDate?: Partial<App.CalendarDay>,
  bypassRegionFilter?: boolean,
  bypassRegionSettingId?: string,
  // When provided (e.g. an add-extra-guests deeplink that carries the requested party size),
  // use this occupancy instead of the booked reservation's. Existing callers omit it and keep
  // the original occupancy.
  occupancyOverride?: App.Occupants,
): App.Checkout.InstantBookingLEHotelItem {
  const pkgOption = orderItem.package as App.OrderItemHotelPackage
  const reservation = orderItem.reservation

  if (!reservation) {
    throw new Error('Reservation is required for package upgrade')
  }

  const occupancy: App.Occupants = occupancyOverride ?? {
    adults: reservation.adults,
    teenagers: reservation.teenagers,
    children: reservation.children,
    infants: reservation.infants,
    childrenAge: reservation.childrenAge,
  }

  return {
    itemId: uuidV4(),
    offerId: offer.id,
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
    reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
    packageId: pkgOption.id,
    roomRateId: pkgOption.roomRateId,
    dynamicPricingRoomTypeId: orderItem.dynamicPricingRoomTypeId,
    dynamicPricingRoomRateId: orderItem.dynamicPricingRoomRateId,
    duration: pkgOption.duration,
    checkIn: selectedDate?.checkIn ?? reservation.startDate,
    checkOut: selectedDate?.checkOut ?? reservation.endDate,
    occupancy,
    orderId: order.id,
    orderItemId: orderItem.id,
    isUpdated: !!selectedDate || !!occupancyOverride,
    // Prefer the durable values persisted on the order item; the params act as a
    // same-session fallback until svc-order returns them.
    bypassRegionFilter: (orderItem.bypassRegionFilter || bypassRegionFilter) || undefined,
    bypassRegionSettingId: orderItem.bypassRegionSettingId || bypassRegionSettingId || undefined,
  }
}
